import React, { useEffect, useState } from "react";
import { Button, Form, Input, DatePicker, Row, Col, Select } from "antd";
import { post } from "../../../../services/services";
import { ApiURL } from "../../../../services/apiConstants";
import dayjs from "dayjs";
import { ErrorToast, SuccessToast } from "../../../../widgets/AppToast";

const SupplyAndDischargeLogForm = ({
  mode = "add",
  odsId: propOdsId,
  logId,
  logs = [],
  readOnly = false,
  isSaved = false,
  onSaveSuccess,
  isStandalone = false,
}) => {
  const [form] = Form.useForm();
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedOdsId, setSelectedOdsId] = useState(propOdsId);

  const designation = JSON.parse(localStorage.getItem("user_data"))?.data
    ?.designation;

  const isElectricalEngineer = designation === "Electrical Engineer";
  const isChiefEngineer = designation === "Chief Engineer";

  const canEditChiefSign = isChiefEngineer && !readOnly;
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isStandalone && mode === "add") {
      const userData = JSON.parse(localStorage.getItem("user_data"));
      const shipId = userData?.data?.ship_details?.ship_id;
      const fd = new FormData();
      fd.append("ship_id", shipId);

      post(ApiURL.list_ods_record, fd).then((res) => {
        if (res?.status) {
          setEquipmentList(res.data);
        }
      });
    }
  }, [isStandalone, mode]);

  /* ---------------- PREFILL ---------------- */
  useEffect(() => {
    if (logs.length > 0) {
      const latest = logs[logs.length - 1];
      form.setFieldsValue({
        date: latest?.date ? dayjs(latest.date) : null,
        supply_of_ods_kgs: latest.supply_of_ods_kgs,
        deliberate_discharge_kgs: latest.deliberate_discharge_kgs,
        non_deliberate_discharge_kgs: latest.non_deliberate_discharge_kgs,
        discharge_to_shore_kgs: latest.discharge_to_shore_kgs,
        remarks: latest.remarks,
        sign_engineer: latest.sign_engineer,
        sign_chief_engineer: latest.sign_chief_engineer,
      });
    }
  }, [logs, form]);

  const appendIfFilled = (fd, key, value) => {
    if (value !== undefined && value !== "" && value !== null) {
      fd.append(key, value);
    }
  };

  const onFinish = async (values) => {
    if (submitting) return;

    const dateValue = values?.date || form.getFieldValue("date");

    if (!dateValue) {
      ErrorToast("Error", "Date is required");
      return;
    }

    // 🔐 Role-based mandatory signature check
    if (!values.sign_engineer) {
      ErrorToast("Error", "operation incharge signature is required");
      return;
    }

    if (canEditChiefSign && !values.sign_chief_engineer) {
      ErrorToast("Error", "Chief Engineer signature is required");
      return;
    }

    setSubmitting(true);

    const fd = new FormData();

    // ✅ ALWAYS REQUIRED
    fd.append("date", dayjs(dateValue).format("YYYY-MM-DD"));

    // ✅ OPTIONAL FIELDS (append ONLY if filled)
    appendIfFilled(fd, "supply_of_ods_kgs", values.supply_of_ods_kgs);
    appendIfFilled(
      fd,
      "deliberate_discharge_kgs",
      values.deliberate_discharge_kgs,
    );
    appendIfFilled(
      fd,
      "non_deliberate_discharge_kgs",
      values.non_deliberate_discharge_kgs,
    );
    appendIfFilled(fd, "discharge_to_shore_kgs", values.discharge_to_shore_kgs);
    appendIfFilled(fd, "remarks", values.remarks);
    fd.append("sign_engineer", values.sign_engineer);

    if (canEditChiefSign) {
      fd.append("sign_chief_engineer", values.sign_chief_engineer);
    }

    // 🔗 API
    const api =
      mode === "edit"
        ? ApiURL.ods_refrigerant_log_update
        : ApiURL.ods_refrigerant_log;

    if (mode === "edit") {
      fd.append("id", logId);
    } else {
      fd.append("id", selectedOdsId || propOdsId);
    }

    const res = await post(api, fd);
    setSubmitting(false);

    const responseData = res?.response?.data || res;

    if (responseData?.status === true) {
      SuccessToast("Success", "ODS Refrigerant Log saved successfully");

      // Notification API Call
      try {
        const notifFd = new FormData();
        notifFd.append("record_id", responseData?.data?.id || responseData?.id || selectedOdsId || propOdsId || "");
        notifFd.append("type", "supply_discharge");
        notifFd.append("description", `New Supply and Discharge Log added`);

        await post(ApiURL.add_refrigeration_ods_notification, notifFd);
      } catch (err) {
        console.error("Failed to send notification:", err);
      }

      // Notify parent that form is saved
      if (typeof onSaveSuccess === "function") {
        onSaveSuccess();
      }
    } else {
      if (responseData?.errors) {
        form.setFields(
          Object.keys(responseData.errors).map((field) => ({
            name: field,
            errors: Array.isArray(responseData.errors[field])
              ? responseData.errors[field]
              : [responseData.errors[field]],
          })),
        );
      } else if (responseData?.error && typeof responseData.error === "string") {
        const fieldMatch = responseData.error.match(/Field '(.*?)'/);
        if (fieldMatch && fieldMatch[1]) {
          form.setFields([
            { name: fieldMatch[1], errors: [responseData.error] },
          ]);
        }
      }
      ErrorToast("Error", responseData?.message || responseData?.error || "Failed to save log");
    }
  };

  useEffect(() => {
    if (!propOdsId || mode !== "add") return;

    const fd = new FormData();
    fd.append("id", propOdsId);

    post(ApiURL.ods_refrigerant_log_get, fd).then((res) => {
      if (res?.status && res.data) {
        form.setFieldsValue({
          ...res.data,
          date: res.data.date ? dayjs(res.data.date) : null,
        });
      }
    });
  }, [propOdsId, mode]);

  return (
    <div className="white-container">
      <h3 className="section-title">ODS Refrigerant Log</h3>

      <Form 
        layout="vertical" 
        form={form} 
        onFinish={onFinish}
        onValuesChange={(changedValues) => {
          const fieldName = Object.keys(changedValues)[0];
          form.setFields([{ name: fieldName, errors: [] }]);
        }}
      >
        {logs.length > 0 &&
          logs[0]?.equipment && ( 
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Equipment">
                  <Input value={logs[0]?.equipment} disabled={readOnly} />
                </Form.Item>
              </Col>
            </Row>
          )}
        {isStandalone && mode === "add" && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Select Equipment"
                name="ods_id"
                rules={[{ required: true, message: "Please select equipment" }]}
              >
                <Select
                disabled={readOnly}
                  placeholder="Select Equipment to add log"
                  onChange={(val) => setSelectedOdsId(val)}
                >
                  {equipmentList?.map((item) => (
                    <Select.Option key={item.ods_id} value={item.ods_id}>
                      {item.equipment} ({item.location_on_board})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        )}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Date"
              name="date"
              rules={[{ required: true, message: "Please select date" }]}
            >
              <DatePicker style={{ width: "100%" }} disabled={readOnly} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item 
              label="Supply of ODS (kgs)" 
              name="supply_of_ods_kgs"
              rules={[
                {
                  pattern: /^[0-9]+(\.[0-9]+)?$/,
                  message: "Please enter a valid number",
                },
              ]}
            >
              <Input disabled={readOnly} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Deliberate Discharge (kgs)"
              name="deliberate_discharge_kgs"
              rules={[
                {
                  pattern: /^[0-9]+(\.[0-9]+)?$/,
                  message: "Please enter a valid number",
                },
              ]}
            >
              <Input disabled={readOnly} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Non-Deliberate Discharge (kgs)"
              name="non_deliberate_discharge_kgs"
              rules={[
                {
                  pattern: /^[0-9]+(\.[0-9]+)?$/,
                  message: "Please enter a valid number",
                },
              ]}
            >
              <Input disabled={readOnly} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Discharge to Shore (kgs)"
              name="discharge_to_shore_kgs"
              rules={[
                {
                  pattern: /^[0-9]+(\.[0-9]+)?$/,
                  message: "Please enter a valid number",
                },
              ]}
            >
              <Input disabled={readOnly} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Remarks" name="remarks">
              <Input disabled={readOnly} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Name of operation incharge"
              name="sign_engineer"
            >
              <Input disabled={readOnly} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Signed by Chief Engineer"
              name="sign_chief_engineer"
              rules={
                canEditChiefSign
                  ? [
                      {
                        required: true,
                        message: "Chief Engineer signature required",
                      },
                    ]
                  : []
              }
              extra={!canEditChiefSign ? "Only Chief Engineer can sign" : ""}
            >
              <Input disabled={!canEditChiefSign} />
            </Form.Item>
          </Col>
        </Row>

         <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            disabled={readOnly}
          >
            Save Supply and Discharge Log
          </Button>
      </Form>
    </div>
  );
};

export default SupplyAndDischargeLogForm;
